import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [isValidSession, setIsValidSession] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState('');
  const [passwordMatchStatus, setPasswordMatchStatus] = useState<'idle' | 'matching' | 'not-matching'>('idle');
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // Listen for password recovery event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsValidSession(true);
        setIsCheckingSession(false);
      } else if (event === 'SIGNED_OUT') {
        setSessionError('Password reset link is invalid or has expired');
        setIsCheckingSession(false);
      }
    });

    // Check current session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setIsValidSession(true);
      } else {
        setSessionError('No valid password reset session found');
      }
      setIsCheckingSession(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const validatePassword = (pwd: string) => {
    const errors = [];
    if (pwd.length < 8) errors.push("at least 8 characters");
    if (!/(?=.*[a-z])/.test(pwd)) errors.push("one lowercase letter");
    if (!/(?=.*[A-Z])/.test(pwd)) errors.push("one uppercase letter");
    if (!/(?=.*\d)/.test(pwd)) errors.push("one number");
    if (!/(?=.*[!@#$%^&*])/.test(pwd)) errors.push("one special character (!@#$%^&*)");
    
    if (errors.length > 0) {
      setPasswordError(`Password must contain ${errors.join(", ")}`);
      return false;
    }
    
    setPasswordError("");
    return true;
  };

  const checkPasswordMatch = () => {
    if (!confirmPassword) {
      setPasswordMatchStatus('idle');
      return true;
    }
    
    if (newPassword === confirmPassword) {
      setPasswordMatchStatus('matching');
      return true;
    } else if (confirmPassword.length >= newPassword.length) {
      setPasswordMatchStatus('not-matching');
      setPasswordError('Passwords do not match');
      return false;
    } else {
      setPasswordMatchStatus('idle');
      return true;
    }
  };

  // Auto-validate password match when either password changes
  useEffect(() => {
    if (newPassword || confirmPassword) {
      checkPasswordMatch();
    } else {
      setPasswordMatchStatus('idle');
      setPasswordError('');
    }
  }, [newPassword, confirmPassword]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password with strict requirements
    if (!validatePassword(newPassword)) {
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "Please make sure both passwords match",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    setIsLoading(false);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Your password has been reset successfully",
    });

    navigate("/timer");
  };

  if (isCheckingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Verifying reset link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (sessionError || !isValidSession) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid Reset Link</CardTitle>
            <CardDescription>{sessionError || 'This password reset link is invalid or has expired'}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full" 
              onClick={() => navigate('/forgot-password')}
            >
              Request New Reset Link
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Reset Password</CardTitle>
          <CardDescription>Enter your new password below</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {passwordError && (
              <p className="text-sm text-destructive">{passwordError}</p>
            )}

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  className={
                    passwordMatchStatus === 'matching'
                      ? "border-green-500 focus:border-green-500"
                      : passwordMatchStatus === 'not-matching'
                      ? "border-red-500 focus:border-red-500"
                      : ""
                  }
                />
                <div className="absolute right-12 top-0 h-full flex items-center">
                  {passwordMatchStatus === 'matching' && (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  )}
                  {passwordMatchStatus === 'not-matching' && (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
              </div>
              {passwordMatchStatus === 'matching' && (
                <p className="text-sm text-green-500 flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Passwords match!
                </p>
              )}
              {passwordMatchStatus === 'not-matching' && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <XCircle className="h-3.5 w-3.5" />
                  Passwords do not match
                </p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting...
                </>
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
